/**
 * The question box.
 *
 * Enter sends, Shift+Enter adds a line — the convention for any multi-line chat input,
 * and the reason this is a textarea rather than a single-line field: questions about
 * data are often two sentences.
 *
 * The keydown listener sits on the wrapper, not the field, for the same reason as in the
 * ⌘K palette: @repo/ui's `Input` accepts `handleKeyDown`, destructures it, and never
 * passes it to the element. Events bubble, and preventDefault still applies from here.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { ArrowUpIcon, MicIcon, SquareIcon } from 'lucide-react'

import { Box, IconButton, Stack, Tooltip, Typography, keyframes, useTheme } from '@mui/material'

import { Alert, Input } from '@repo/ui'
import { useAskRunner, useAskStore } from 'ask'
import { useShortcutBindings } from 'hooks'
import { eventMatchesBinding } from 'utils'

/**
 * The Web Speech API's shape, trimmed to the four members this composer actually
 * touches. There is no `@types` package for it in this repo (it never shipped as
 * part of the DOM lib — it is still non-standard), and pulling one in for four
 * fields is not worth a dependency for something already feature-detected at the
 * call site.
 */
interface DictationResultAlternative {
  transcript: string
}
interface DictationResult {
  isFinal: boolean
  [index: number]: DictationResultAlternative
}
interface DictationResultList {
  length: number
  [index: number]: DictationResult
}
interface DictationEvent {
  resultIndex: number
  results: DictationResultList
}
interface DictationErrorEvent {
  error: string
}
interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((event: DictationEvent) => void) | null
  onerror: ((event: DictationErrorEvent) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

/**
 * `SpeechRecognition` on Chrome/Edge, `webkitSpeechRecognition` on Safari — neither
 * is on `Window` in TypeScript's DOM lib, hence the cast. Returns `null` (rather than
 * throwing) on a browser that has neither, which is what lets the mic button decide
 * whether to render at all: a control that silently does nothing on click is worse
 * than no control (Firefox has no implementation at all as of this writing).
 */
const getSpeechRecognitionCtor = (): SpeechRecognitionConstructor | null => {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

type DictationState = 'idle' | 'recording' | 'transcribing'

/**
 * Whether the browser will re-prompt for the mic (VRG-227).
 *
 * `prompt` and `granted` both mean the NEXT click either already works or will raise a
 * real browser prompt — nothing to do there. `denied` is the dead end: once a site is
 * denied, no page can re-trigger that prompt or navigate to the browser's own settings
 * (both are deliberately blocked), so this is what decides whether the blocked-mic
 * message below gets a way forward or is just informational.
 */
type MicPermissionState = 'unknown' | 'prompt' | 'granted' | 'denied'

/** A slow, calm pulse — not a spinner. Pure CSS, so it still advances under a
 *  headless capture that never fires `requestAnimationFrame` (see `AskMessageList`'s
 *  `shimmerSweep` for the same reasoning). Respects `prefers-reduced-motion` below. */
const dictationPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`

export const AskComposer = () => {
  const theme = useTheme()
  const [value, setValue] = useState('')
  const { send, stop, busy, configured, access } = useAskRunner()
  // VRG-677: no permission to use Ask Vera (inside a brand's space, the brand has not
  // granted it to Vera support). The box stays VISIBLE but disabled, with the reason
  // both inline and as the tooltip: permission disables, it never hides (§27/§46).
  const blockedReason = access.message

  // The panel can be opened with a question already carried over from search. The runner
  // sends it; this just clears anything half-typed so the box does not sit stale
  // alongside a question that is already on its way.
  const pendingQuestion = useAskStore(s => s.pendingQuestion)
  useEffect(() => {
    if (pendingQuestion) setValue('')
  }, [pendingQuestion])

  // The inner <textarea>, not the wrapper — needed to focus it and place the caret
  // after a suggestion fills the box (see the `draftQuestion` effect below).
  const textareaRef = useRef<HTMLInputElement>(null)

  /**
   * A suggestion clicked in the empty-state carousel (VRG-227). Unlike `pendingQuestion`
   * above, this must NOT send — it only fills the box, so the user can send as-is or
   * adjust first. See `draftQuestion`'s docblock in useAskStore for why this is a
   * separate channel from the palette's send-immediately one.
   *
   * Two effects, not one, because focusing has to happen AFTER the textarea's DOM value
   * actually reflects the new text — `setSelectionRange` against the stale (pre-update)
   * value would place the caret at the wrong offset (or before an empty string).
   * `useLayoutEffect` keyed on `focusPending` fires synchronously right after the commit
   * that wrote the new value into the DOM, which is exactly the guarantee needed —
   * `requestAnimationFrame` would also work in a real browser but never fires in the
   * Claude preview pane (see AskSuggestedQuestions' docblock), so this avoids depending
   * on it at all.
   */
  const draftQuestion = useAskStore(s => s.draftQuestion)
  const consumeDraftQuestion = useAskStore(s => s.consumeDraftQuestion)
  const [focusPending, setFocusPending] = useState(false)

  useEffect(() => {
    if (draftQuestion === null) return
    setValue(draftQuestion)
    consumeDraftQuestion()
    setFocusPending(true)
  }, [draftQuestion, consumeDraftQuestion])

  useLayoutEffect(() => {
    if (!focusPending) return
    const el = textareaRef.current
    if (el) {
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
    setFocusPending(false)
  }, [focusPending])

  const submit = () => {
    const q = value.trim()
    if (!q || busy || blockedReason) return
    setValue('')
    void send(q)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const canSend = value.trim().length > 0 && !busy && !blockedReason

  // --- Dictation (VRG-227) ---------------------------------------------------------
  // Feature-detected once — this never changes mid-session, and re-running the two
  // `window` property reads on every render buys nothing.
  const SpeechRecognitionCtor = useMemo(getSpeechRecognitionCtor, [])
  const dictationSupported = SpeechRecognitionCtor !== null

  const [dictationState, setDictationState] = useState<DictationState>('idle')
  const [dictationError, setDictationError] = useState<string | null>(null)
  const [micPermission, setMicPermission] = useState<MicPermissionState>('unknown')

  /**
   * Feature-detect `navigator.permissions.query({name: 'microphone'})` so a `denied`
   * mic can be told apart from `prompt` up front, rather than only learning it from a
   * failed `start()` call. Safari's support here is partial (it does not recognise the
   * `'microphone'` descriptor at all as of this writing) — the `.catch` below leaves
   * `micPermission` at `'unknown'` there, and the `onerror` handler further down is
   * what still catches a real denial on that browser.
   */
  useEffect(() => {
    if (!dictationSupported || typeof navigator === 'undefined' || !navigator.permissions?.query) return

    let cancelled = false
    let status: PermissionStatus | null = null

    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then(result => {
        if (cancelled) return
        status = result
        setMicPermission(result.state as MicPermissionState)
        result.onchange = () => {
          const next = result.state as MicPermissionState
          setMicPermission(next)
          // The permission flipping to anything other than 'denied' means the user just
          // fixed it (e.g. from the browser's own site-settings UI) — clear the blocked
          // message ourselves rather than leaving it stuck until they retry or reload.
          if (next !== 'denied') setDictationError(null)
        }
      })
      .catch(() => {
        // Browser does not support querying this permission — leave it at 'unknown'.
      })

    return () => {
      cancelled = true
      if (status) status.onchange = null
    }
  }, [dictationSupported])
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  // `onresult` closes over whatever `value` was at the moment `start()` was called,
  // and a long dictation session can fire more than one final result — a plain
  // closure would silently drop every chunk but the last. The ref is always current.
  const valueRef = useRef(value)
  valueRef.current = value

  // Belt-and-braces: if the composer unmounts mid-recording (closing the panel),
  // stop the microphone rather than leaving it listening in the background.
  useEffect(() => () => recognitionRef.current?.abort(), [])

  /**
   * The actual Web Speech setup + `start()` call, factored out of `startDictation` so it
   * can be reached from two paths: straight away (permission already `granted`, or the
   * browser can't be asked explicitly at all) or after an explicit `getUserMedia` request
   * resolves (see `startDictation` below).
   */
  const beginRecognition = useCallback(() => {
    if (!SpeechRecognitionCtor) return

    const recognition = new SpeechRecognitionCtor()
    recognition.lang = navigator.language || 'en-US'
    recognition.continuous = true
    recognition.interimResults = false

    recognition.onresult = event => {
      let transcript = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) transcript += result[0].transcript
      }
      transcript = transcript.trim()
      if (!transcript) return
      // Appended into the box for the user to review and send themselves — dictation
      // must never submit on their behalf. Putting words in someone's mouth and
      // sending them without a chance to check is the failure mode here.
      const next = valueRef.current ? `${valueRef.current} ${transcript}` : transcript
      valueRef.current = next
      setValue(next)
    }

    recognition.onerror = event => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setDictationError(
          'Microphone access is blocked. Look for a mic or camera icon next to your browser’s address bar, allow access, then try again.',
        )
        // A real `not-allowed` failure IS a denial regardless of what the Permissions
        // API said (or whether it exists at all — see the effect above) — this is the
        // fallback source of truth on Safari, and belt-and-braces everywhere else.
        setMicPermission('denied')
      } else if (event.error !== 'aborted' && event.error !== 'no-speech') {
        // 'no-speech' fires whenever the mic goes quiet for a moment and is not an
        // error worth surfacing — recording just ends and the button returns to idle.
        setDictationError('Dictation stopped unexpectedly. Try again.')
      }
      setDictationState('idle')
      recognitionRef.current = null
    }

    recognition.onend = () => {
      setDictationState('idle')
      recognitionRef.current = null
    }

    recognitionRef.current = recognition
    setDictationState('recording')
    recognition.start()
  }, [SpeechRecognitionCtor])

  const startDictation = useCallback(() => {
    if (!SpeechRecognitionCtor || dictationState !== 'idle') return
    setDictationError(null)

    // `recognition.start()` alone raises the browser's mic prompt as a SIDE EFFECT — that
    // is implicit, and gives us nothing to react to except a later `onerror`. Asking via
    // `getUserMedia` first is explicit: it gives a real promise to resolve/reject on, and
    // (per Permissions API semantics) it is also what actually flips `micPermission` from
    // `prompt` to `granted`/`denied` in the first place.
    const canRequestMic = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia

    // Already granted → skip straight to `start()`. Re-requesting here would just be a
    // wasted round trip (`getUserMedia` resolves instantly, but still costs a promise tick
    // and a moment of the mic indicator lighting before we stop it again).
    //
    // No `getUserMedia` at all → this is an insecure origin (non-localhost HTTP), where the
    // API is not exposed. Fall back to the old implicit behaviour rather than doing nothing.
    if (micPermission === 'granted' || !canRequestMic) {
      beginRecognition()
      return
    }

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        // Stop every track immediately. Holding the stream open for the life of the
        // dictation session would leave the browser's own recording indicator lit the
        // whole time — `SpeechRecognition` opens and manages its own capture separately,
        // so this stream has done its only job (proving/obtaining permission) as soon as
        // it resolves.
        stream.getTracks().forEach(track => track.stop())
        beginRecognition()
      })
      .catch(() => {
        // Denied (or dismissed) at the explicit prompt. `micPermission` may already be
        // `'denied'` here (e.g. a second click after a prior denial, where `getUserMedia`
        // rejects immediately with no re-prompt) — setting it again is harmless.
        setMicPermission('denied')
        setDictationError(
          'Microphone access is blocked. Look for a mic or camera icon next to your browser’s address bar, allow access, then try again.',
        )
      })
  }, [SpeechRecognitionCtor, dictationState, micPermission, beginRecognition])

  const stopDictation = useCallback(() => {
    if (!recognitionRef.current) return
    // 'transcribing' covers the gap between asking the API to stop and its `onend`
    // actually firing — the API still delivers a final `onresult` for whatever was
    // said just before `stop()`, so the button reads as busy rather than idle for
    // that brief window instead of looking like the click did nothing.
    setDictationState('transcribing')
    recognitionRef.current.stop()
  }, [])

  const toggleDictation = useCallback(() => {
    if (dictationState === 'idle') startDictation()
    else if (dictationState === 'recording') stopDictation()
    // 'transcribing': ignore extra clicks until it settles back to idle on its own.
  }, [dictationState, startDictation, stopDictation])

  // The dictation shortcut is the one binding in the catalog that must fire WHILE
  // this field has focus — that is where it is used — so it is matched directly here
  // rather than through the shared, `isTypingTarget`-guarded listener the panel's
  // other two shortcuts use (see `useAskShortcuts`).
  const { askDictation } = useShortcutBindings()
  useEffect(() => {
    if (!dictationSupported || blockedReason) return
    const onDictationKeyDown = (event: KeyboardEvent) => {
      if (!eventMatchesBinding(event, askDictation)) return
      event.preventDefault()
      toggleDictation()
    }
    window.addEventListener('keydown', onDictationKeyDown)
    return () => window.removeEventListener('keydown', onDictationKeyDown)
  }, [askDictation, dictationSupported, toggleDictation, blockedReason])

  const dictationTooltip =
    dictationState === 'recording'
      ? 'Stop dictation'
      : dictationState === 'transcribing'
        ? 'Transcribing…'
        : 'Start dictation'

  return (
    <Box
      onKeyDown={onKeyDown}
      sx={{
        flexShrink: 0,
        px: 4,
        py: 3,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* `flex-start`, not `flex-end`: the send control reads as an action on the
          question, not a footer of the box — it should sit level with the first line
          of typed text, the same way a paper plane sits at the top of a chat composer,
          rather than chasing the textarea's bottom edge as it grows to `maxRows`. */}
      {blockedReason && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mb: 3 }}
          description={blockedReason}
        />
      )}
      <Stack direction="row" spacing={3} alignItems="flex-start">
        {/* A disabled field emits no hover events, so the Box is the tooltip's target. */}
        <Tooltip title={blockedReason ?? ''} disableHoverListener={!blockedReason}>
        <Box sx={{ flex: 1, minWidth: 0, cursor: blockedReason ? 'not-allowed' : undefined }}>
          <Input
            disabled={!!blockedReason}
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            inputRef={textareaRef}
            value={value}
            onChange={setValue}
            // TODO(VRG-444 / VRG-445): market and benchmark data are NOT built. This
            // placeholder names them on the user's explicit instruction, because they
            // intend to land both in the same session. It must not reach `main` ahead of
            // them — a placeholder advertising a capability that does not exist is the
            // failure mode working-conventions §30 describes, and the user cannot tell
            // the difference between "not built" and "broken".
            //
            // ⚠️ SECOND SURFACE, added later: the search overlay's Ask row secondary
            // ("Opens the panel to ask anything about Vera, your data, the market and
            // more") now makes the same claim, deliberately, so the two agree. Keep them
            // in sync, and hold BOTH until VRG-444/445 land — the exposure is now two
            // surfaces, not one. See `SearchOverlay.tsx`'s matching note.
            placeholder="Ask about your data, the market, benchmarks, or how Vera works"
            inputProps={{ 'aria-label': 'Your question' }}
            /**
             * No fill override. The composer takes the shared `fieldFill` like every other
             * input in the app.
             *
             * It briefly needed one: while the panel sat on `sidebarSurface`, that token
             * and `fieldFill` were the same `#fafafa` in light mode, so the field was
             * exactly its own background. Moving the panel to the card surface removed the
             * collision at its source, which is the better fix — a context-specific field
             * fill was a workaround for a surface chosen one level too high.
             */
          />
        </Box>
        </Tooltip>

        {/*
          Mic + send/stop as ONE pair, in their own row, so the two read as a unit
          rather than as two independently-positioned controls (VRG-227 polish pass —
          they had drifted out of horizontal alignment with each other). `alignItems:
          center` centres them against EACH OTHER directly, rather than relying on the
          outer Stack's `flex-start` (shared with the textarea) to happen to line them
          up. `mt` moves here, off the individual buttons, for the same reason: one
          offset for the pair instead of two that have to be kept in sync by hand.
        */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, flexShrink: 0 }}>
          {/* Mic sits before the send/stop control — hidden entirely (not merely
              disabled) on a browser with no Web Speech implementation, per the brief:
              a mic that silently does nothing on click is worse than none. */}
          {dictationSupported && (
            <Tooltip title={dictationTooltip}>
              <span>
                <IconButton
                  onClick={toggleDictation}
                  disabled={dictationState === 'transcribing' || !!blockedReason}
                  aria-label={dictationTooltip}
                  aria-pressed={dictationState === 'recording'}
                  sx={{
                    border: `1px solid ${theme.palette.primary[100]}`,
                    color: dictationState === 'recording' ? theme.palette.error.main : theme.palette.text.secondary,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      animation: dictationState === 'recording' ? `${dictationPulse} 1.4s ease-in-out infinite` : 'none',
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }}
                  >
                    <MicIcon strokeWidth={1} size={16} />
                  </Box>
                </IconButton>
              </span>
            </Tooltip>
          )}

          {busy ? (
            <Tooltip title="Stop">
              <IconButton
                onClick={stop}
                aria-label="Stop answering"
                sx={{ border: `1px solid ${theme.palette.primary[100]}` }}
              >
                <SquareIcon strokeWidth={1} size={16} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={blockedReason ?? ''} disableHoverListener={!blockedReason}>
            <span>
            <IconButton
              onClick={submit}
              disabled={!canSend}
              aria-label="Send question"
              sx={{
                /**
                 * INVERTED against the surface, per theme — not a mode-invariant white.
                 *
                 * This was pinned to `common.white` in BOTH modes, with a long argument
                 * for why (the glyph and hover had to be pinned too, since `primary[950]`
                 * and `primary[200]` both reverse in dark). Every step of that reasoning
                 * was about DARK mode, where a white disc on a near-black panel is right.
                 * Light mode was never checked: the panel is `#fafafa`, so a white button
                 * on it measured 1.04:1 — the fill was invisible and only the black arrow
                 * floated there. Against a 3:1 floor for a control's own boundary, on the
                 * panel's primary action.
                 *
                 * So it inverts: near-black disc with a white glyph in light, white disc
                 * with a near-black glyph in dark. The point was always "maximum contrast
                 * against the surface", and mode-invariance only delivered that in one of
                 * the two modes. `common.black`/`common.white` stay the tokens because
                 * they are the two that do NOT reverse — the reversal is now explicit and
                 * intended rather than something to be defended against.
                 */
                bgcolor: canSend
                  ? theme.palette.mode === 'light'
                    ? theme.palette.common.black
                    : theme.palette.common.white
                  : 'transparent',
                color: canSend
                  ? theme.palette.mode === 'light'
                    ? theme.palette.common.white
                    : theme.palette.common.black
                  : 'text.disabled',
                border: `1px solid ${
                  canSend
                    ? theme.palette.mode === 'light'
                      ? theme.palette.common.black
                      : theme.palette.common.white
                    : theme.palette.primary[100]
                }`,
                // Hover nudges toward the surface it sits on, in both directions: a light
                // control lightens, a dark one darkens. Literal hexes for the same reason
                // the fills are `common.*` — the neutral scales reverse in dark mode and
                // would send the hover the wrong way.
                '&:hover': {
                  bgcolor: canSend ? (theme.palette.mode === 'light' ? '#2a2a2e' : '#ececec') : 'transparent',
                },
              }}
            >
              <ArrowUpIcon strokeWidth={1} size={16} />
            </IconButton>
            </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {dictationError && (
        // Shared @repo/ui Alert, `warning` + the default `outlined` (tinted, not filled)
        // variant — a proper warning rather than plain red body text, but subtle enough to
        // sit inside the composer without shouting over the question box above it.
        //
        // Dismissal has two paths, neither of which requires a reload: the X (`onClose`)
        // clears it by hand, and the permission-change effect above (`result.onchange`)
        // clears it automatically the moment the browser reports anything other than
        // `denied` — e.g. the user fixes it from the browser's own site-settings UI while
        // this panel is still open.
        //
        // No link is offered here. It was "Contact support if you need help turning it back
        // on" — the wrong destination: support cannot flip a browser's own permission, and
        // neither re-raising the prompt nor navigating to the browser's site-settings page is
        // something a page is allowed to do once a user has denied it (both are deliberately
        // blocked). No dedicated "how to re-enable your mic" guide exists in `docs/guides/`
        // today either, so rather than link to one that doesn't exist (a dead end this same
        // session hit more than once elsewhere), `dictationError`'s own copy is written to
        // stand alone: it names where the toggle usually lives (the icon beside the address
        // bar) without promising a page. If a guide is written later, link it from here.
        <Alert
          severity="warning"
          variant="outlined"
          onClose={() => setDictationError(null)}
          sx={{ mt: 2 }}
          description={dictationError}
        />
      )}

      {/*
        Sending is deliberately still ENABLED with no provider configured. Disabling it
        would leave someone typing into a dead box with no explanation; letting the turn
        run produces an explicit, visible error that says exactly what is missing. The
        notice below sets that expectation before they type, so the error is confirmation
        rather than a surprise.
      */}
      {!configured && (
        <Typography variant="body4" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}>
          No answer provider is connected yet, so questions can't be answered. What you
          type is kept in this conversation.
        </Typography>
      )}
    </Box>
  )
}
